import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Calendra2 } from "@common/DatePicker";
import ReactSelect from "@common/ReactSelect";
import { MAX_MOBILE_LENGTH } from "@src/config/constant";
import { ValidateFileInput } from "@src/config/helper";
import { getCityList, getLocalityList, setDistrict } from "@store/slices/commonId";
import Accordion from "react-bootstrap/Accordion";
import {
    createRegisterUploadItem,
    flattenSectionFields,
    getComparableFieldValue,
    getFieldDisplayLabel,
    getFieldKey,
    getUploadFileName,
    getUploadPreviewSrc,
    hasFieldValue,
    isUploadImageFile,
    normalizeOptions,
    revokeRegisterUploadPreview,
    sanitizeRegisterTextValue,
} from "./registerFormUtils";

const FIELD_ROW_CLASS = "col-md-12 col-xs-12";
const NPWP_FORMATTED_MAX_LENGTH = 20;

const isUploadPdfFile = (fileItem: any) => {
    const rawFile = fileItem?.file instanceof File ? fileItem.file : fileItem;

    if (rawFile instanceof File) {
        return rawFile.type === "application/pdf" || rawFile.name.toLowerCase().endsWith(".pdf");
    }

    if (typeof fileItem?.type === "string" && fileItem.type.toLowerCase().includes("pdf")) {
        return true;
    }

    const fileName = `${getUploadFileName(fileItem) || fileItem?.file_name || fileItem?.src || fileItem?.file_url || ""}`
        .split("?")[0]
        .toLowerCase();

    return fileName.endsWith(".pdf");
};

const getUploadOpenSrc = (fileItem: any) => {
    if (typeof fileItem === "string") {
        return fileItem;
    }

    return fileItem?.preview_url || fileItem?.src || fileItem?.file_url || "";
};

const buildAccordionGroups = (sectionItem: any) => {
    const groupedSections: any[] = [];

    (sectionItem?.blocks || []).forEach((blockItem: any, blockIndex: number) => {
        const visibleFields = (blockItem?.fields || []).filter((fieldItem: any) => fieldItem?.is_show !== false);

        if (!visibleFields.length) {
            return;
        }

        const hasLabelSections = visibleFields.some((fieldItem: any) => fieldItem?.type === "label");

        if (!hasLabelSections) {
            groupedSections.push({
                key: blockItem?.key || `block_${blockIndex}`,
                title: blockItem?.title || `Section ${groupedSections.length + 1}`,
                fields: visibleFields.filter((fieldItem: any) => fieldItem?.type !== "label"),
            });
            return;
        }

        let currentGroup: any = null;

        visibleFields.forEach((fieldItem: any, fieldIndex: number) => {
            if (fieldItem?.type === "label") {
                currentGroup = {
                    key: `${blockItem?.key || `block_${blockIndex}`}_group_${fieldIndex}`,
                    title: getFieldDisplayLabel(fieldItem) || blockItem?.title || `Section ${groupedSections.length + 1}`,
                    fields: [],
                };
                groupedSections.push(currentGroup);
                return;
            }

            if (!currentGroup) {
                currentGroup = {
                    key: `${blockItem?.key || `block_${blockIndex}`}_group_fallback`,
                    title: blockItem?.title || `Section ${groupedSections.length + 1}`,
                    fields: [],
                };
                groupedSections.push(currentGroup);
            }

            currentGroup.fields.push(fieldItem);
        });
    });

    return groupedSections.filter((groupItem: any) => groupItem?.title && (groupItem?.fields || []).length);
};

const RegisterSectionRenderer = (props: any) => {
    const {
        sectionItem,
        sectionIndex,
        updateSection,
        onNext,
        setLoading,
        requestHeaders,
        renderBlocksAsAccordion = false,
        accordionDefaultOpenKeys = [],
        accordionDefaultOpenAll = false,
        scrollClass = "",
        resolveFieldOptions,
    } = props;

    const dispatch: any = useDispatch();
    const commonState = useSelector((state: any) => state.common);
    const commonIdState = useSelector((state: any) => state.commonId);
    const currentFields = flattenSectionFields(sectionItem);
    const accordionGroups = renderBlocksAsAccordion ? buildAccordionGroups(sectionItem) : [];
    const defaultOpenKeySignature = (accordionDefaultOpenKeys || []).join("|");
    const getInitialAccordionKeys = () => {
        if ((accordionDefaultOpenKeys || []).length) {
            return (accordionDefaultOpenKeys || []).map((keyItem: string | number) => String(keyItem));
        }

        if (accordionDefaultOpenAll) {
            return accordionGroups.map((_groupItem: any, groupIndex: number) => String(groupIndex));
        }

        return [];
    };
    const [openAccordionKeys, setOpenAccordionKeys] = useState<string[]>(getInitialAccordionKeys);

    useEffect(() => {
        setOpenAccordionKeys(getInitialAccordionKeys());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionItem?.key, renderBlocksAsAccordion, accordionGroups.length, accordionDefaultOpenAll, defaultOpenKeySignature]);

    const getOptionsForField = (fieldItem: any) => {
        const resolvedOptions = resolveFieldOptions?.(fieldItem, {
            commonState,
            commonIdState,
            currentFields,
            sectionItem,
        });

        if (resolvedOptions !== undefined) {
            return normalizeOptions(resolvedOptions);
        }

        const commonData = commonState?.common || {};
        let options = fieldItem?.dynamicOptions?.length ? fieldItem.dynamicOptions : fieldItem?.options || [];

        if (fieldItem?.type === "all_city") {
            options = commonIdState?.city || [];
        } else if (fieldItem?.type === "state") {
            options = commonIdState?.state || [];
        } else if (fieldItem?.type === "city") {
            options = fieldItem?.dynamicOptions || [];
        } else if (fieldItem?.type === "locality") {
            options = fieldItem?.dynamicOptions?.length ? fieldItem.dynamicOptions : commonIdState?.district || [];
        } else if (fieldItem?.master_data_key && commonData?.[fieldItem.master_data_key]) {
            options = commonData[fieldItem.master_data_key];
        } else if (fieldItem?.name === "gender" && commonData?.gender) {
            options = commonData.gender;
        } else if (commonData?.[fieldItem?.name]) {
            options = commonData[fieldItem.name];
        }

        return normalizeOptions(options);
    };

    const isFieldDisabled = (fieldItem: any) => {
        if (fieldItem?.is_enable === false) {
            return true;
        }

        if (!fieldItem?.parent_depedencies) {
            return false;
        }

        const parentField = currentFields.find((currentField: any) => currentField?.name === fieldItem.parent_depedencies);
        return !hasFieldValue(parentField);
    };

    const updateCurrentSectionField = (fieldItem: any, value: any) => {
        const childDependencies = Array.isArray(fieldItem?.child_depedencies)
            ? fieldItem.child_depedencies
            : fieldItem?.child_depedencies
                ? [fieldItem.child_depedencies]
                : [];

        updateSection(sectionIndex, (currentSection: any) => {
            const nextSection = structuredClone(currentSection);

            nextSection?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((currentField: any) => {
                    if (currentField?.name === fieldItem?.name) {
                        currentField.value = value;
                        currentField.error_message = "";
                    }

                    if (childDependencies.includes(currentField?.name)) {
                        currentField.value = "";
                        currentField.error_message = "";
                    }
                });
            });

            return nextSection;
        });
    };

    const toSelectOptions = (list: any[] = []) => {
        return list.map((item: any) => {
            const numericValue = Number(item?.value);
            const optionValue = Number.isFinite(numericValue) ? numericValue : item?.id;

            return {
                label: item?.label || item?.value,
                value: Number(optionValue),
            };
        });
    };

    const updateDynamicOptions = (fieldType: string, options: any[]) => {
        updateSection(sectionIndex, (currentSection: any) => {
            const nextSection = structuredClone(currentSection);

            nextSection?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((currentField: any) => {
                    if (currentField?.type === fieldType) {
                        currentField.dynamicOptions = options;
                        currentField.error_message = "";
                    }
                });
            });

            return nextSection;
        });
    };

    const handleTextChange = (fieldItem: any, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let { value } = event.target;
        const mobileMaxLength = fieldItem?.validation?.max_length || MAX_MOBILE_LENGTH;

        value = sanitizeRegisterTextValue(fieldItem, value);

        if (fieldItem?.type === "mobile" && value.length > mobileMaxLength) {
            return;
        }

        const maxLength = fieldItem?.name === "npwp_no" ? NPWP_FORMATTED_MAX_LENGTH : fieldItem?.validation?.max_length;
        if (maxLength && value.length > maxLength) {
            value = value.slice(0, maxLength);
        }

        updateCurrentSectionField(fieldItem, value);
    };

    const handleSelectChange = async (selectedOption: any, fieldItem: any) => {
        updateCurrentSectionField(fieldItem, selectedOption);

        if (fieldItem?.type === "state") {
            dispatch({ type: setDistrict.type, payload: [] });
            if (!selectedOption?.value) {
                return;
            }

            setLoading?.(true);
            const response: any = await dispatch(getCityList({ state: [selectedOption.value] }, requestHeaders));
            updateDynamicOptions("city", toSelectOptions(response?.data || []));
            setLoading?.(false);
            return;
        }

        if (fieldItem?.type !== "city") {
            return;
        }

        dispatch({ type: setDistrict.type, payload: [] });

        if (!selectedOption?.value) {
            return;
        }

        setLoading?.(true);
        const response: any = await dispatch(getLocalityList({ city: [selectedOption.value] }, requestHeaders));
        updateDynamicOptions("locality", toSelectOptions(response?.data || []));
        setLoading?.(false);
    };

    const handleDateChange = (fieldItem: any, dateValue: Date | null) => {
        updateCurrentSectionField(fieldItem, dateValue);
    };

    const handleRadioChange = (fieldItem: any, optionValue: any) => {
        updateCurrentSectionField(fieldItem, optionValue);
    };

    const handleFileUpload = async (fieldItem: any, event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = "";

        if (!selectedFiles.length) {
            return;
        }

        const isFileInvalid = await ValidateFileInput(selectedFiles);
        if (isFileInvalid) {
            return;
        }

        updateSection(sectionIndex, (currentSection: any) => {
            const nextSection = structuredClone(currentSection);

            nextSection?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((currentField: any) => {
                    if (currentField?.name !== fieldItem?.name) {
                        return;
                    }

                    const previousFiles = currentField?.uploaded_files || [];
                    const maxUpload = currentField?.max_upload || previousFiles.length + selectedFiles.length;
                    const normalizedFiles = selectedFiles.map((selectedFile: File) => createRegisterUploadItem(selectedFile));
                    const combinedFiles = [...previousFiles, ...normalizedFiles];
                    const droppedFiles = combinedFiles.slice(maxUpload);
                    droppedFiles.forEach((fileItem: any) => revokeRegisterUploadPreview(fileItem));
                    currentField.uploaded_files = combinedFiles.slice(0, maxUpload);
                    currentField.error_message = "";
                });
            });

            return nextSection;
        });
    };

    const removeUploadedFile = (fieldItem: any, fileIndex: number) => {
        updateSection(sectionIndex, (currentSection: any) => {
            const nextSection = structuredClone(currentSection);

            nextSection?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((currentField: any) => {
                    if (currentField?.name !== fieldItem?.name) {
                        return;
                    }

                    const removedFile = currentField?.uploaded_files?.[fileIndex];
                    revokeRegisterUploadPreview(removedFile);
                    currentField.uploaded_files = (currentField?.uploaded_files || []).filter(
                        (_item: any, currentIndex: number) => currentIndex !== fileIndex
                    );
                    currentField.error_message = "";
                });
            });

            return nextSection;
        });
    };

    const openUploadedFile = (fileItem: any) => {
        const rawFile = fileItem?.file instanceof File ? fileItem.file : null;
        const fileUrl = rawFile ? URL.createObjectURL(rawFile) : getUploadOpenSrc(fileItem);

        if (!fileUrl) {
            return;
        }

        window.open(fileUrl, "_blank", "noopener,noreferrer");

        if (rawFile) {
            setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
        }
    };

    const renderUploadedFileCard = (fieldItem: any, fileItem: any, fileIndex: number) => {
        const previewSrc = getUploadPreviewSrc(fileItem);
        const isImageUpload = isUploadImageFile(fileItem);
        const isPdfUpload = isUploadPdfFile(fileItem);

        return (
            <div
                key={`${getFieldKey(fieldItem, fileIndex)}_upload_${fileIndex}`}
                style={{
                    position: "relative",
                    width: "100%",
                    height: "150px",
                    minHeight: "150px",
                    background: "#fff",
                    border: "1px solid #D4E0EB",
                    borderRadius: "8px",
                    padding: "12px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center"
                }}
            >
                <button
                    type="button"
                    onClick={() => removeUploadedFile(fieldItem, fileIndex)}
                    aria-label={`Remove ${getFieldDisplayLabel(fieldItem)}`}
                    style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        width: "22px",
                        height: "22px",
                        border: "none",
                        borderRadius: "50%",
                        background: "#111827",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                    }}
                >
                    <i className="ic-clearclose" style={{ fontSize: "10px" }}></i>
                </button>

                <div className="d-flex flex-column align-items-center justify-content-center" style={{ width: "100%", height: "100%" }}>
                    {isImageUpload && previewSrc ? (
                        <img
                            src={previewSrc}
                            alt={getFieldDisplayLabel(fieldItem)}
                            onClick={() => openUploadedFile(fileItem)}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "6px",
                                cursor: "pointer"
                            }}
                        />
                    ) : (
                        <>
                            {isPdfUpload ? (
                                <span
                                    onClick={() => openUploadedFile(fileItem)}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <i
                                        className="ic-pdf-file icons-normal"
                                        style={{
                                            fontSize: "48px",
                                            color: "#008CF0",
                                            lineHeight: 1,
                                            display: "block",
                                            transform: "translateX(4px)",
                                        }}
                                    ></i>
                                </span>
                            ) : (
                                <>
                                    <i
                                        className="ic-insert_drive_file"
                                        onClick={() => openUploadedFile(fileItem)}
                                        style={{ fontSize: "28px", color: "#008CF0", marginBottom: "8px", cursor: "pointer" }}
                                    ></i>
                                    <span style={{ fontSize: "10px", lineHeight: "14px", color: "#111827", wordBreak: "break-word" }}>
                                        {getUploadFileName(fileItem)}
                                    </span>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderUploadField = (fieldItem: any) => {
        const uploadedFiles = fieldItem?.uploaded_files || [];
        const maxUpload = fieldItem?.max_upload || 1;
        const hasUploadedFiles = hasFieldValue(fieldItem);

        return (
            <div key={getFieldKey(fieldItem)} className={`${FIELD_ROW_CLASS} m-lg-b doc-upload-outer-id`}>
                <div className="faq-wrapper resummison-outer document-upload-acc-outer">
                  <div className="document-card animation-effect bank-detail-acc">
    <Accordion>
        <Accordion.Item eventKey={getFieldKey(fieldItem, 0)}>

            {/* HEADER */}
            <Accordion.Header>
                <div
                    className="summery-heading upload-heading-text document-upload-id w-100"
                    style={{
                        background: fieldItem?.color_code || "#FFF1D1",
                    }}
                >
                   
                        <span
                            className="doc-upload-icn"
                           
                        >
                            {fieldItem?.logo ? (
                                <img
                                    src={fieldItem.logo}
                                    alt={fieldItem?.label || "document"}
                                    
                                />
                            ) : (
                                <i
                                    className="ic-insert_drive_file"
                                ></i>
                            )}
                        </span>
                    <div>
                        <h3>
                            {getFieldDisplayLabel(fieldItem)}
                        </h3>
                    </div>
                    </div>
                {/* RIGHT SIDE */}
                {hasUploadedFiles ? (
                    <div className="check-filed-icn">
                        <i className="ic-check"></i>
                    </div>
                ) : null}
            </Accordion.Header>

            {/* BODY */}
            <Accordion.Body>
                <div
                   
                >
                  <div className="image-outer-sec" style={{ background: "#EEF7FF" }}>
                    <div className="row p-lg-t p-lg-b" style={{ rowGap: "16px"}}>
                            {Array.from({ length: maxUpload }).map((_item, uploadIndex) => {
                                const uploadedFile = uploadedFiles[uploadIndex];

                                return (
                                    <div
                                        key={`${getFieldKey(fieldItem, uploadIndex)}_slot_${uploadIndex}`}
                                        className="col-md-4 col-xs-4"
                                    >
                                        {!uploadedFile ? (
                                            <div
                                                className="add-document-proof"
                                                role="presentation"
                                            
                                            >
                                                <input
                                                    type="file"
                                                    className="upload-image-input"
                                                    accept="image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx"
                                                    disabled={uploadedFiles.length >= maxUpload}
                                                    onChange={(event) =>
                                                        handleFileUpload(fieldItem, event)
                                                    }
                                                />
                                                <i
                                                    className="ic-add"
                                                
                                                ></i>
                                                <span
                                                
                                                >
                                                    Upload
                                                </span>
                                            </div>
                                        ) : (
                                            renderUploadedFileCard(
                                                fieldItem,
                                                uploadedFile,
                                                uploadIndex
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {fieldItem?.error_message && (
                        <span className="error-txt">
                            {fieldItem.error_message}
                        </span>
                    )}
                </div>
            </Accordion.Body>

        </Accordion.Item>
    </Accordion>
</div>
                </div>
            </div>
        );
    };

    const renderField = (fieldItem: any, fieldIndex: number) => {
        if (fieldItem?.is_show === false) {
            return null;
        }

        const fieldValue = fieldItem?.value ?? "";
        const optionList = getOptionsForField(fieldItem);
        const selectedOptionValue = getComparableFieldValue(fieldValue);
        const selectedOption = typeof fieldValue === "object"
            ? fieldValue
            : optionList.find((optionItem: any) => String(optionItem?.value) === String(selectedOptionValue)) || null;
        const displayLabel = getFieldDisplayLabel(fieldItem);
        const isDisabled = isFieldDisabled(fieldItem);
        const inputMaxLength =
            fieldItem?.name === "npwp_no"
                ? NPWP_FORMATTED_MAX_LENGTH
                : fieldItem?.validation?.max_length || undefined;

        switch (fieldItem?.type) {
            case "label":
                return (
                    <div key={getFieldKey(fieldItem, fieldIndex)} className={FIELD_ROW_CLASS}>
                        <div className="summery-heading">
                            <h2>{displayLabel}</h2>
                        </div>
                    </div>
                );

            case "dd":
            case "sdd":
            case "state":
            case "city":
            case "locality":
            case "all_city":
                return (
                    <div key={getFieldKey(fieldItem, fieldIndex)} className={FIELD_ROW_CLASS}>
                        <ReactSelect
                            options={optionList}
                            value={selectedOption}
                            handleChange={(selectedValue: any) => handleSelectChange(selectedValue, fieldItem)}
                            placeholder={displayLabel}
                            fieldKey={fieldItem?.name}
                            fieldSetClassName="m-sm-b"
                            innerClass={`material animation-effect ${selectedOption ? "active-label" : ""}`}
                            errorField={fieldItem?.error_message}
                            isDisabled={isDisabled}
                        />
                    </div>
                );

            case "radio":
                return (
                    <div key={getFieldKey(fieldItem, fieldIndex)} className={`${FIELD_ROW_CLASS} m-xl-b radio-btn`}>
                        <div className="m-sm-b p-sm-t">{displayLabel}</div>
                        <div className="d-flex" style={{ flexWrap: "wrap", gap: "16px" }}>
                            {optionList.map((optionItem: any, optionIndex: number) => {
                                const radioId = `${fieldItem?.name || "radio"}_${optionIndex}`;
                                const checkedValue = getComparableFieldValue(fieldItem?.value);

                                return (
                                    <div key={radioId} className="custom-control custom-checkbox">
                                        <input
                                            checked={String(checkedValue) === String(optionItem?.value)}
                                            id={radioId}
                                            type="radio"
                                            className="custom-control-input"
                                            name={fieldItem?.name || radioId}
                                            onChange={() => handleRadioChange(fieldItem, optionItem?.value)}
                                        />
                                        <label className="custom-control-label" htmlFor={radioId}>{optionItem?.label}</label>
                                    </div>
                                );
                            })}
                        </div>
                        {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                    </div>
                );

            case "date":
                return (
                    <fieldset key={getFieldKey(fieldItem, fieldIndex)} className={`${FIELD_ROW_CLASS} form-filed date-picker-detail`}>
                        <div className={`material animation-effect ${fieldItem?.value ? "active-label" : ""}`}>
                            <Calendra2
                                onChange={(dateValue: Date) => handleDateChange(fieldItem, dateValue)}
                                placeholder={displayLabel}
                                dateFormat="dd/MM/yyyy"
                                selected={fieldItem?.value || null}
                                fieldLabel={displayLabel}
                                disabled={isDisabled}
                                fieldname={fieldItem?.name}
                            />
                            {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                        </div>
                    </fieldset>
                );

            case "image":
                return renderUploadField(fieldItem);

            default:
                return (
                    <fieldset key={getFieldKey(fieldItem, fieldIndex)} className={FIELD_ROW_CLASS}>
                        <div className={`material ${fieldItem?.error_message ? "error" : ""}`}>
                            <input
                                type={fieldItem?.name === "email" ? "email" : "text"}
                                inputMode={fieldItem?.type === "mobile" || fieldItem?.keyboard_type === "number-pad" ? "numeric" : undefined}
                                placeholder=" "
                                name={fieldItem?.name}
                                value={fieldValue || ""}
                                className="form-input"
                                onChange={(event) => handleTextChange(fieldItem, event)}
                                disabled={isDisabled}
                                maxLength={inputMaxLength}
                            />
                            <label data-label={displayLabel} className="form-label"></label>
                            {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                        </div>
                    </fieldset>
                );
        }
    };

    const renderBlockFields = (blockItem: any, blockIndex: number) => {
        return blockItem?.fields?.map((fieldItem: any, fieldIndex: number) =>
            renderField(fieldItem, blockIndex * 1000 + fieldIndex)
        );
    };

    const renderAccordionGroupFields = (groupItem: any, groupIndex: number) => {
        return (groupItem?.fields || []).map((fieldItem: any, fieldIndex: number) =>
            renderField(fieldItem, groupIndex * 1000 + fieldIndex)
        );
    };

    const toggleAccordionBlock = (blockIndex: number) => {
        const blockKey = String(blockIndex);

        setOpenAccordionKeys((prevState: string[]) => {
            if (prevState.includes(blockKey)) {
                return prevState.filter((currentKey: string) => currentKey !== blockKey);
            }

            return [...prevState, blockKey];
        });
    };

    return (
        <div className="login-form" >
            <form autoComplete="off">
                {renderBlocksAsAccordion ? (
                    <div className="row">
                        <div className={`register-scroll-outer ${scrollClass}`}>
                        {accordionGroups.map((groupItem: any, groupIndex: number) => {
                            const groupKey = String(groupIndex);
                            const isOpen = openAccordionKeys.includes(groupKey);

                            return (
                                <div className="personal-detail-accordian" key={groupItem?.key || `accordion_group_${groupIndex}`}>
                                    <button
                                        type="button"
                                        onClick={() => toggleAccordionBlock(groupIndex)}
                                        aria-expanded={isOpen}
                                        className="col-md-12"
                                    >
                                        <span>
                                            {groupItem?.title || `Section ${groupIndex + 1}`}
                                        </span>
                                        <span
                                            aria-hidden="true"
                                         
                                        >
                                            {isOpen ? "-" : "+"}
                                        </span>
                                    </button>

                                    {isOpen ? (
                                        <div className="detail-filed-outer">
                                            {renderAccordionGroupFields(groupItem, groupIndex)}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    </div>
                ) : (
                    sectionItem?.blocks?.map((blockItem: any, blockIndex: number) => (
                        <div key={blockItem?.key || `block_${blockIndex}`}>
                            {blockItem?.title ? <div className="upload-sec-heading">{blockItem.title}</div> : null}
                           <div className="row">
                                <div className="register-scroll-outer">
                                    {renderBlockFields(blockItem, blockIndex)}
                                </div>
                            </div>
                        </div>
                    ))
                )}

                <div className="btn-bottom-fixed p-lg-t btn-full">
                    <button type="button" className="btn-primary" onClick={onNext}>
                        {sectionItem?.btn?.title || "Continue"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default RegisterSectionRenderer;
